/**
Phase-locked oscilloscope data for the shaders' iChannel0 time-domain rows.

An analyser's waveform is whatever the last fftSize samples happened to be, so drawn as-is
it slides sideways every frame. A plain trigger (the first rising zero crossing) steadies a
sine but not a mix: in a General MIDI arrangement the treble crosses zero all over the
place, and which crossing comes first changes frame to frame, so the wave still shimmies.

This locks it the way corrscope does:
  - only rising zero crossings of a LOW-PASSED copy of the signal are trigger candidates,
    so it steps in whole cycles of the bass rather than in treble wiggles;
  - of those, it takes the one whose window best matches what the previous frame showed
    (normalised correlation), so a held note stays put in phase and a new one re-locks.

It reads the analyser's fftSize samples, locks a window of half of them, and hands that
window back as `width` texels (the texture width), starting at the chosen crossing, so
there is always a full window after it. A longer analyser than 2 * width (the scope pair in
lib/fx.civet is 4096 against a 512-texel texture) is averaged down, `factor` samples per
texel: a longer window holds whole bass cycles, and the average keeps what it drops from
aliasing. Both channels are cut at the same offset from the mono sum, so the stereo pair
stays aligned.

Frame history makes it stateful: one instance per texture, fed frames in order. Offline
renders feed frames in order, so the result is still deterministic.
*/

// One-pole low-pass coefficient: ~200 Hz at 48 kHz. Low enough that a bass or low-mid
// fundamental sets the crossings; the exact cutoff only moves which cycle is chosen.
LOWPASS := 0.026

type FloatSource =
  // Absent on stand-ins, which are then taken to be 2 * width long.
  fftSize?: number
  getFloatTimeDomainData: (out: Float32Array) => void

// The mapping getByteTimeDomainData itself uses: 128 is silence, 128 * (1 + v).
toByte := (v: number) ->
  Math.max 0, Math.min 255, Math.round 128 * (1 + v)

export phaseLock := (width: number) ->
  factor .= 0
  size .= 0
  span .= 0
  l .= new Float32Array 0
  r .= new Float32Array 0
  mono .= new Float32Array 0
  low .= new Float32Array 0
  prev .= new Float32Array 0
  havePrev .= false

  /** The chosen start offset, in samples, for tests and diagnostics. */
  lock := (left: FloatSource, right: FloatSource, outL: Uint8Array, outR: Uint8Array): number ->
    f := Math.max 1, Math.floor (left.fftSize ?? 2 * width) / (2 * width)
    // (Re)size on the first frame, or if the source changes length.
    unless f is factor
      factor = f
      span = width * factor
      size = span * 2
      l = new Float32Array size
      r = new Float32Array size
      mono = new Float32Array size
      low = new Float32Array size
      prev = new Float32Array span
      havePrev = false

    left.getFloatTimeDomainData l
    right.getFloatTimeDomainData r

    for i of [0...size]
      mono[i] = l[i] + r[i]
    // Warm start from the opening average: from 0, the filter's own settling (~40 samples)
    // crosses zero whenever the buffer opens positive, which is a trigger on nothing.
    y .= 0
    for i of [0...64]
      y += mono[i] / 64
    for i of [0...size]
      y += LOWPASS * (mono[i] - y)
      low[i] = y

    best .= -1
    bestScore .= -Infinity
    for k of [1...span]
      continue unless low[k - 1] <= 0 < low[k]
      // First frame: nothing to match yet, so the plain first-crossing trigger.
      unless havePrev
        best = k
        break
      dot .= 0
      energy .= 0
      for i of [0...span]
        v := mono[k + i]
        dot += v * prev[i]
        energy += v * v
      score := if energy > 0 then dot / Math.sqrt energy else 0
      if score > bestScore
        bestScore = score
        best = k

    // No rising crossing at all (silence, or a wave slower than the window): don't jump.
    offset := if best < 0 then 0 else best

    for i of [0...width]
      sl .= 0
      sr .= 0
      for j of [0...factor]
        sl += l[offset + i * factor + j]
        sr += r[offset + i * factor + j]
      outL[i] = toByte sl / factor
      outR[i] = toByte sr / factor
    for i of [0...span]
      prev[i] = mono[offset + i]
    havePrev = true

    offset
